import axios from 'axios';
import { getToken } from './authService';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';
const client = axios.create({ baseURL: API_BASE });
client.interceptors.request.use((cfg) => {
  const token = getToken();
  if (token) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

export async function listDigitalProfiles() {
  const { data } = await client.get('/digital-profiles');
  return data.profiles || [];
}

export async function getDigitalProfile(id) {
  const { data } = await client.get(`/digital-profiles/${id}`);
  return data;
}

export async function runProfileAction(id, action, target = null) {
  const { data } = await client.post(`/digital-profiles/${id}/action`, { action, target });
  return data;
}

export async function chatToProfile(id, message) {
  const { data } = await client.post(`/digital-profiles/${id}/chat`, { message });
  return data;
}

export async function directorBroadcast(message) {
  const { data } = await client.post('/digital-profiles/director', { message });
  return data;
}

export async function amplifyPost(payload) {
  const { data } = await client.post('/digital-profiles/amplify', payload);
  return data;
}

// Reusa endpoints de dummy-accounts (los perfiles digitales son la misma tabla).
export async function createDigitalProfile(payload) {
  const { data } = await client.post('/dummy-accounts', payload);
  return data.account;
}

export async function setProfileSessionid(id, sessionid) {
  const { data } = await client.post(`/dummy-accounts/${id}/set-sessionid`, { sessionid });
  return data.account;
}

export async function deleteDigitalProfile(id) {
  await client.delete(`/dummy-accounts/${id}`);
}

export async function batchCreateProfiles(accounts) {
  const { data } = await client.post('/dummy-accounts/batch', { accounts });
  return data;
}

// Crea un perfil e inmediatamente intenta extraer cookies automáticamente.
export async function createAndExtractProfile(payload) {
  const { data } = await client.post('/dummy-accounts/create-and-extract', payload);
  return data;
}

export async function verifyProfileCredentials(id) {
  const { data } = await client.post('/perfiles-ia/verify', { id });
  return data;
}

export async function getProfileStatus(id) {
  const { data } = await client.get(`/dummy-accounts/${id}`);
  return data;
}

export async function listProfileTasks(id) {
  const { data } = await client.get(`/digital-profiles/${id}/tasks`);
  return data; // { tasks, summary }
}

// Guarda credenciales de login (email/usuario + password) cifradas en el server.
export async function updateProfileCredentials(id, { login_user, password, totp_secret, email_imap_password, email_imap_host, email_address }) {
  const payload = {};
  if (login_user !== undefined) payload.login_user = login_user;
  if (password !== undefined) payload.password = password;
  if (totp_secret !== undefined) payload.totp_secret = totp_secret;
  if (email_imap_password !== undefined) payload.email_imap_password = email_imap_password;
  if (email_imap_host !== undefined) payload.email_imap_host = email_imap_host;
  if (email_address !== undefined) payload.email_address = email_address;
  const { data } = await client.put(`/dummy-accounts/${id}`, payload);
  return data.account;
}

// Inicia/renueva la sesión usando las credenciales guardadas.
export async function reloginProfile(id) {
  const { data } = await client.post(`/digital-profiles/${id}/relogin`, {});
  return data; // { ok, status?, error?, account }
}

// Login con navegador real en el server. Puede devolver { challenge, challengeId }.
export async function browserLogin(id) {
  const { data } = await client.post(`/digital-profiles/${id}/browser-login`, {});
  return data;
}

// Ingresa el código de verificación del login con navegador.
export async function browserLoginCode(id, challengeId, code) {
  const { data } = await client.post(`/digital-profiles/${id}/browser-login-code`, { challengeId, code });
  return data;
}

export async function clearProfileTasks(id) {
  const { data } = await client.post(`/digital-profiles/${id}/tasks`, { op: 'clear' });
  return data;
}

// Registro automático de cuenta nueva en X/Twitter (email temporal + Playwright).
export async function autoRegisterProfile({ platform = 'x', count = 1 } = {}) {
  const { data } = await client.post('/dummy-accounts/auto-register', { platform, count });
  return data;
}

// Extracción automática de cookies: login via Playwright con credenciales guardadas.
export async function autoExtractCookies(id) {
  const { data } = await client.post(`/dummy-accounts/${id}/auto-extract-cookies`);
  return data;
}

// Limpieza de cuentas dummy sin actividad real.
// dryRun: true = solo muestra, no elimina
// minDays: días sin actividad para considerar inactiva
export async function cleanupDummyAccounts({ dryRun = true, minDays = 7 } = {}) {
  const { data } = await client.post('/dummy-accounts/cleanup', { dryRun, minDays });
  return data;
}

// Activar/Desactivar actividad automática en cuentas dummy
// action: 'start' | 'stop'
// accountIds: array de IDs
// interval: ms entre actividades (default 30000)
export async function activateDummyAccounts({ action = 'start', accountIds = [], interval = 30000 } = {}) {
  const { data } = await client.post('/dummy-accounts/activate', { action, account_ids: accountIds, interval });
  return data;
}

// Obtener logs de actividad de cuentas dummy
// accountId: ID específico o null para todas
// since: timestamp ISO para logs nuevos
// limit: máximo de logs
export async function getDummyActivityLogs({ accountId = null, since = null, limit = 50 } = {}) {
  const params = new URLSearchParams();
  if (accountId) params.append('account_id', accountId);
  if (since) params.append('since', since);
  params.append('limit', limit);
  
  const { data } = await client.get(`/dummy-accounts/logs?${params.toString()}`);
  return data;
}

// Obtener credenciales de cuentas dummy (solo testing)
export async function getDummyCredentials() {
  const { data } = await client.get('/dummy-accounts/credentials');
  return data;
}

// Test login de una cuenta dummy
export async function testLoginProfile(id) {
  const { data } = await client.post(`/dummy-accounts/${id}/test-login`);
  return data;
}

// Crea cuentas de email temporales reales via proveedor gratuito (mail.tm) en el servidor.
// Preferentemente emula un navegador real (screenshot-server /create-email).
// body: { count, useBrowser?, preferredDomain? }
export async function createEmailAccounts({ count = 1, useBrowser = true, preferredDomain = null, provider = 'mailtm' } = {}) {
  const { data } = await client.post('/email-accounts/batch-create', { count, useBrowser, preferredDomain, provider });
  return data;
}

export async function listEmailAccounts({ limit = 100, status = null } = {}) {
  const { data } = await client.get('/email-accounts', { params: { limit, status } });
  return data;
}

const digitalProfilesService = {
  listDigitalProfiles,
  getDigitalProfile,
  runProfileAction,
  chatToProfile,
  directorBroadcast,
  amplifyPost,
  createDigitalProfile,
  setProfileSessionid,
  deleteDigitalProfile,
  batchCreateProfiles,
  verifyProfileCredentials,
  getProfileStatus,
  listProfileTasks,
  clearProfileTasks,
  updateProfileCredentials,
  reloginProfile,
  browserLogin,
  browserLoginCode,
  autoRegisterProfile,
  autoExtractCookies,
  cleanupDummyAccounts,
  activateDummyAccounts,
  getDummyActivityLogs,
  getDummyCredentials,
  testLoginProfile,
  createEmailAccounts,
  listEmailAccounts,
};
export default digitalProfilesService;
